<template>
  <div class="home-container" itemscope itemtype="https://schema.org/Person">
    <!-- Hero Main Section -->
    <section class="home-grid">
      <!-- Left Column: Copy & Actions -->
      <div class="home-copy">
        <!-- Status Eyebrow Badge -->
        <div :class="['status-pill', showContent ? 'animate-in' : 'opacity-0']">
          <span class="status-text">Available for Freelance &amp; Full-Time</span>
        </div>

        <!-- Hero Title -->
        <h1 :class="['hero-title', showContent ? 'animate-in delay-1' : 'opacity-0']" itemprop="name">
          Building <span class="highlight-coral">Tactile</span> &amp; Modern Web Experiences.
        </h1>

        <!-- Typewriter Role Subtitle -->
        <div class="py-1">
          <TypeWriter 
            :phrases="roles"
            :period="2000"
            :class="[showContent ? 'animate-in delay-2' : 'opacity-0']"
          />
          <meta itemprop="jobTitle" content="Front-End Web Developer">
        </div>

        <!-- Description -->
        <p :class="['hero-description', showContent ? 'animate-in delay-2' : 'opacity-0']" itemprop="description">
          Hi! I’m <strong class="text-neo-black">Reynaldi Siregar</strong>, a front-end developer and UI crafter based in Bekasi, Indonesia. I bridge creative design and high-performance engineering to build web apps that feel fast, intuitive, and delightful.
        </p>

        <!-- CTA Buttons -->
        <div class="hero-actions" :class="showContent ? 'animate-in delay-3' : 'opacity-0'">
          <ResumeButton :resume-path="resumePath" />
          <router-link to="/portfolio" class="secondary-cta">
            <span>Explore Work</span>
            <svg class="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
            </svg>
          </router-link>
        </div>
      </div>

      <!-- Right Column: Profile Showcase -->
      <div class="profile-showcase" :class="showContent ? 'animate-in delay-2' : 'opacity-0'">
        <div class="profile-card-wrapper">
          <!-- Background Crisscross Blue Layer -->
          <div class="profile-card-backdrop" aria-hidden="true"></div>

          <!-- Main Profile Photo Card -->
          <div class="profile-card">
            <div class="profile-image-frame">
              <img 
                alt="Reynaldi Wiratama Siregar - Front-End Web Developer" 
                fetchpriority="high" 
                width="340" 
                height="340" 
                decoding="async" 
                class="profile-picture" 
                src="/img/profile.webp"
                itemprop="image"
              >
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Interactive Highlights Ticker / Skill Bar -->
    <section class="ticker-section" :class="showContent ? 'animate-in delay-3' : 'opacity-0'">
      <div class="ticker-box">
        <div class="ticker-header">
          <span class="ticker-label">[ CORE FOCUS ]</span>
        </div>
        <div class="ticker-tags">
          <span class="tech-chip chip-yellow">
            <svg class="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
            Vue.js 3
          </span>
          <span class="tech-chip chip-blue">
            <svg class="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"/>
            </svg>
            UI/UX Architecture
          </span>
          <span class="tech-chip chip-coral">
            <svg class="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z"/>
            </svg>
            Tailwind CSS
          </span>
          <span class="tech-chip chip-mint">
            <svg class="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
            Performance First
          </span>
          <span class="tech-chip chip-purple">
            <svg class="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
            Reactive Systems
          </span>
          <span class="tech-chip chip-pink">
            <svg class="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"/>
            </svg>
            Responsive Layouts
          </span>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import TypeWriter from '../components/TypeWriter.vue'
import ResumeButton from '../components/ResumeButton.vue'

export default defineComponent({
  name: 'HomeView',
  components: {
    TypeWriter,
    ResumeButton
  },
  props: {
    showContent: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      roles: [
        "Front-End Web Developer",
        "Founder @ RND Kreatif",
        "UI/UX Builder & Crafter",
        "Information Systems Student",
      ],
      resumePath: '/CV_REYNALDI_SIREGAR.pdf',
    }
  }
})
</script>

<style scoped>
.home-container {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}

.home-grid {
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 3rem;
  align-items: center;
  min-height: calc(100vh - 12rem);
}

.home-copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.35rem;
}

/* Status Pill */
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.4rem 0.9rem;
  background-color: #D1FAE5;
  border: 2px solid #121212;
  border-radius: 999px;
  box-shadow: 2px 2px 0 #121212;
}

.status-text {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.76rem;
  font-weight: 700;
  color: #065F46;
  letter-spacing: 0.02em;
}

/* Hero Title */
.hero-title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(2.4rem, 4.8vw, 4.2rem);
  font-weight: 900;
  line-height: 1.04;
  letter-spacing: -0.04em;
  color: #121212;
}

.highlight-coral {
  display: inline-block;
  background: #FFE600;
  padding: 0 0.25em;
  border: 2px solid #121212;
  border-radius: 8px;
  box-shadow: 3px 3px 0 #121212;
  transform: rotate(-1.5deg);
}

/* Hero Description */
.hero-description {
  font-size: clamp(1rem, 1.4vw, 1.12rem);
  line-height: 1.65;
  color: #374151;
  max-width: 36rem;
  font-weight: 500;
}

/* Hero Actions */
.hero-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  margin-top: 0.5rem;
}

.secondary-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.85rem 1.4rem;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.92rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #121212;
  background-color: #FFFFFF;
  border: 2.5px solid #121212;
  border-radius: 14px;
  text-decoration: none;
  box-shadow: 4px 4px 0 #121212;
  transform: translate(0, 0);
  transition: all 0.12s cubic-bezier(0.16, 1, 0.3, 1);
  cursor: pointer;
}

.secondary-cta:hover {
  transform: translate(2px, 2px);
  box-shadow: 2px 2px 0 #121212;
}

.secondary-cta:active {
  transform: translate(4px, 4px);
  box-shadow: 0px 0px 0 #121212;
}

/* Profile Bento Showcase */
.profile-showcase {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 1rem;
}

.profile-card-wrapper {
  position: relative;
  width: 100%;
  max-width: 340px;
}

.profile-card-backdrop {
  position: absolute;
  inset: -4px;
  background-color: #3B82F6;
  background-image: 
    repeating-linear-gradient(45deg, rgba(18, 18, 18, 0.2) 0, rgba(18, 18, 18, 0.2) 2px, transparent 0, transparent 12px),
    repeating-linear-gradient(-45deg, rgba(18, 18, 18, 0.2) 0, rgba(18, 18, 18, 0.2) 2px, transparent 0, transparent 12px);
  border: 2.5px solid #121212;
  border-radius: 20px;
  transform: rotate(4deg);
  z-index: 1;
}

.profile-card {
  position: relative;
  z-index: 2;
  padding: 0.85rem;
  background: #FFFFFF;
  border: 2.5px solid #121212;
  border-radius: 20px;
  transform: rotate(-2deg);
  transition: transform 0.2s ease;
}

.profile-card:hover {
  transform: rotate(0deg);
}

.profile-image-frame {
  position: relative;
  border: 2px solid #121212;
  border-radius: 14px;
  overflow: hidden;
  background-color: #F4EFE6;
}

.profile-picture {
  display: block;
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.profile-card:hover .profile-picture {
  transform: scale(1.03);
}

/* Ticker Section */
.ticker-section {
  width: 100%;
  margin-top: 1rem;
}

.ticker-box {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  padding: 1.25rem 1.5rem;
  background: #FFFFFF;
  border: 2.5px solid #121212;
  border-radius: 18px;
  box-shadow: 5px 5px 0 #121212;
}

.ticker-header {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.72rem;
  font-weight: 800;
  color: #4B5563;
  letter-spacing: 0.08em;
}

.ticker-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  align-items: center;
}

.tech-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.85rem;
  border: 2px solid #121212;
  border-radius: 999px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  box-shadow: 2px 2px 0 #121212;
  transition: all 0.15s ease;
  cursor: default;
}

.tech-chip:hover {
  transform: translate(-1.5px, -1.5px);
  box-shadow: 3px 3px 0 #121212;
}

.chip-yellow { background-color: #FEF08A; color: #121212; }
.chip-blue { background-color: #DBEAFE; color: #1E40AF; }
.chip-coral { background-color: #FFE4E6; color: #9F1239; }
.chip-mint { background-color: #D1FAE5; color: #065F46; }
.chip-purple { background-color: #EDE9FE; color: #5B21B6; }
.chip-pink { background-color: #FCE7F3; color: #9D174D; }

/* Entrance Animations */
.animate-in {
  animation: neoFadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.delay-1 { animation-delay: 0.08s; }
.delay-2 { animation-delay: 0.16s; }
.delay-3 { animation-delay: 0.24s; }

@keyframes neoFadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

/* Responsive */
@media (max-width: 960px) {
  .home-grid {
    grid-template-columns: 1fr;
    gap: 2.5rem;
    padding-top: 1rem;
  }

  .home-copy {
    align-items: flex-start;
  }

  .profile-showcase {
    order: -1;
    padding: 1.5rem 0;
  }

  .profile-card-wrapper {
    max-width: 280px;
  }
}

@media (max-width: 640px) {
  .hero-actions {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }

  .hero-actions > * {
    width: 100%;
  }

  .sticker {
    font-size: 0.68rem;
    padding: 0.35rem 0.6rem;
  }

  .ticker-box {
    padding: 1rem;
  }
}
</style>
